<template>
  <div id="yearwise-efficiency-list">
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Dialogs -->
    <v-dialog v-model="AddRecordPrompt" persistent max-width="1300px">
      <yearwise-efficiency-create :AddRecordPrompt="AddRecordPrompt" @hideDialog="hideAddRecordPrompt"
        v-if="AddRecordPrompt"></yearwise-efficiency-create>
    </v-dialog>

    <v-dialog v-model="EditRecordPrompt" persistent max-width="1300px">
      <yearwise-efficiency-edit :EditRecordPrompt="EditRecordPrompt" :recordData="selectedData"
        @hideDialog="hideEditRecordPrompt" v-if="EditRecordPrompt"></yearwise-efficiency-edit>
    </v-dialog>

    <!-- Premium Header Card -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <!-- Left Side: Title and Icon -->
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="40px" color="white">mdi-calendar-check</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-0">
                  Yearwise Efficiency
                </h1>
                <p class="header-subtitle white--text mb-0">
                  Manage efficiency points allocations for each year
                </p>
              </div>
            </v-col>

            <!-- Right Side: Action Buttons -->
            <v-col cols="12" md="4" class="text-right">
              <v-btn icon class="action-btn" @click="refreshPageData" title="Refresh">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>

              <v-btn v-if="tableOptions1.CreateButtonFlag" icon class="action-btn ml-2" @click="AddRecordPrompt = true"
                title="Add 100% Efficiency">
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>

              <router-link to="/criteria/settings" v-if="tableOptions1.ListButtonFlag" class="text-decoration-none">
                <v-btn icon class="action-btn ml-2" title="Criteria Settings">
                  <v-icon>mdi-tune</v-icon>
                </v-btn>
              </router-link>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Search & Filter Card -->
    <v-card elevation="12" class="search-card mb-6">
      <v-container class="pa-6">
        <v-form ref="form1" v-model="valid1" lazy-validation v-on:submit.prevent="searchForm">
          <v-row align="center">
            <v-col cols="12" sm="6" md="4">
              <div class="form-field-wrapper">
                <label class="form-label">
                  <v-icon small color="#2B9EB3">mdi-calendar</v-icon>
                  <span class="text-danger">*</span> Year
                </label>
                <v-autocomplete :reduce="(option) => option.value" :items="JciYearCodeOptions" :rules="JciYearCodeRules"
                  :loading="JciYearCodeOptionsLoading" v-model="JciYearCode" dense clearable required outlined
                  class="custom-input" placeholder="Select Year">
                  <template v-slot:prepend-inner>
                    <v-icon color="#2B9EB3" small>mdi-calendar-range</v-icon>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-btn :disabled="!valid1" @click.prevent="searchForm" :loading="SearchFlag" color="primary" elevation="4"
                large block class="search-btn mt-6">
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card>

    <!-- Data Table Card -->
    <v-card elevation="12" class="table-card" v-if="tableData1.length > 0">
      <v-container class="pa-6">
        <v-row class="mb-4">
          <v-col cols="12" class="d-flex align-center justify-space-between">
            <div>
              <h3 class="table-title">
                <v-icon class="mr-2">mdi-table-large</v-icon>
                Efficiency Records ({{ tableData1.length }})
              </h3>
            </div>
            <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Search in table" single-line
              hide-details dense outlined class="search-field" style="max-width: 300px"></v-text-field>
          </v-col>
        </v-row>

        <v-data-table class="efficiency-table" v-model="selected" :headers="tableColumns1" :items="tableData1"
          :single-select="tableOptions1.SingleSelectFlag" :search="search"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100, -1] }" :item-key="tableOptions1.ItemKey" multi-sort>
          <!-- Active Status Column -->
          <template v-slot:item.ActiveStatusTxt="{ item }">
            <v-chip :color="getActiveStatusColor(item.ActiveStatusTxt)" text-color="white" small
              class="font-weight-bold">
              <v-icon left small>{{
                item.ActiveStatusTxt === "Active"
                  ? "mdi-check-circle"
                  : "mdi-close-circle"
              }}</v-icon>
              {{ item.ActiveStatusTxt }}
            </v-chip>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <div class="action-cell">
              <v-tooltip bottom v-if="item.EditFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn x-small icon color="primary" @click="editData(item)" v-bind="attrs" v-on="on"
                    class="action-icon-btn">
                    <v-icon small>mdi-pencil-circle</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.DeleteFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn x-small icon color="error" @click="deleteConfirmAlert(item)" v-bind="attrs" v-on="on"
                    class="action-icon-btn ml-1">
                    <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-container>
    </v-card>

    <!-- Empty State -->
    <v-card elevation="12" class="empty-state-card" v-else-if="!LoadingFlag && SearchFlag">
      <v-container class="pa-12 text-center">
        <v-icon size="80" color="grey lighten-2">mdi-calendar-remove</v-icon>
        <h3 class="mt-4 text-h6 grey--text text--darken-2">No records found</h3>
        <p class="mt-2 text-body2 grey--text">
          Try selecting a different year or creating a new record
        </p>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import YearwiseEfficiencyCreate from "@/view/pages/erp/efficiency/YearwiseEfficiencyCreate.vue";
import YearwiseEfficiencyEdit from "@/view/pages/erp/efficiency/YearwiseEfficiencyEdit.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: { YearwiseEfficiencyCreate, YearwiseEfficiencyEdit },
  data() {
    return {
      LoadingFlag: false,
      SearchFlag: false,
      uploadPercentage: 0,
      AddRecordPrompt: false,
      EditRecordPrompt: false,

      PageTitle: "",
      PageDescription: "",

      selected: [],
      selectedData: {},

      valid1: true,
      OverlayLoadingFlag: false,

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCode: "",
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      search: "",
      CurrentYearId: 0,

      PageInfo: {},
      JciYearCode: "",
      YearName: "",
    };
  },
  mounted() { },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        this.CurrentYearId = CurrentJciYearId;

        this.getTableRecords();
        this.getJciYearCodeOptions();
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.JciYearCodeOptionsLoading = false;
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "full_efficiency",
        Action: "list_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getJciYearCodeOptions() {
      console.log("getJciYearCodeOptions called");
      this.JciYearCodeOptionsLoading = true;
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 3,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    searchForm() {
      console.log("searchForm is called");

      var validate = this.$refs.form1.validate();
      console.log("validate=" + validate);
      if (validate) {
        this.getTableRecords();
      } else {
        var message = "";
        if (!validate) {
          message = "Kindy fill required fields and then submit";
        }
        this.sweetAlert("error", message, false);
      }
    },
    getTableRecords() {
      console.log("getTableRecords is called");
      this.LoadingFlag = true;
      this.SearchFlag = true;
      this.tableData1 = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/yearwise-efficiency/lists";
      var upload = {
        UserInterface: 1,
        Year: this.JciYearCode,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.LoadingFlag = false;
          thisIns.SearchFlag = false; // Changed this to keep search active visually if needed, but per logic typically we keep data

          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);

          console.log({ records });

          var options = records.TableOptions;
          console.log({ options });
          thisIns.tableOptions1 = options;

          if (flag == 1) {
            thisIns.tableColumns1 = records.TableHeader;
            thisIns.tableData1 = records.TableData;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          thisIns.LoadingFlag = false;
          thisIns.SearchFlag = false;

          console.log("error=" + error);
          thisIns.toast("error", error);
        });
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.AddRecordPrompt = false;
      this.getTableRecords();
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.EditRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one efficiency to edit";
        this.toast("error", message);
      }
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.EditRecordPrompt = false;
      this.getTableRecords();
    },
    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var message = "";
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/> Criteria Name <b> " + tr.CriteriaName + "</b>";
        htmlTxt +=
          "<br/> Efficiency Points <b> " + tr.EfficiencyPoints + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteData(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    deleteData(data) {
      console.log("deleteData is called");

      var YearwiseEfficiencyCode = data.YearwiseEfficiencyId;
      var YearCode = data.YearCode;
      var CriteriaCode = data.CriteriaCode;
      var PointsType = data.PointsType;
      console.log(
        "YearwiseEfficiencyCode=" +
        YearwiseEfficiencyCode +
        "; YearCode=" +
        YearCode +
        "; CriteriaCode=" +
        CriteriaCode +
        "; PointsType=" +
        PointsType
      );

      if (YearwiseEfficiencyCode > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/yearwise-efficiency/destroy";
        var upload = {
          UserInterface: 1,
          YearwiseEfficiency: YearwiseEfficiencyCode,
          Year: YearCode,
          PointsType: PointsType,
          Criteria: CriteriaCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "Efficiency should not be empty. ";
        this.sweetAlert("error", message, false);
      }
    },
    getActiveStatusColor(status) {
      if (status === "Active") return "#4caf50";
      return "#f44336";
    }
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss">
// Color Palette - JCI Brand Colors
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$danger-color: #e74c3c;
$border-color: #e2e8f0;
$success-color: #4caf50;

// ============================================
// KEYFRAME ANIMATIONS
// ============================================
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-12px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

// ============================================
// MAIN CONTAINER STYLES
// ============================================
#yearwise-efficiency-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(135deg,
        $navy 0%,
        $aqua 50%,
        $navy 100%) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(45deg,
          transparent 30%,
          rgba(255, 255, 255, 0.1) 50%,
          transparent 70%);
      animation: shimmer 3s infinite;
    }

    .header-gradient {
      position: relative;
      z-index: 1;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle,
          rgba(255, 255, 255, 0.15) 0%,
          transparent 70%);
      animation: rotate 25s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: linear-gradient(135deg,
        rgba(255, 255, 255, 0.25) 0%,
        rgba(255, 255, 255, 0.15) 100%);
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // ============================================
  // SEARCH CARD
  // ============================================
  .search-card {
    border-radius: 16px !important;
    background: #ffffff !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.08) !important;
    border: none !important;
  }

  .form-field-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-label {
    font-size: 14px;
    font-weight: 700;
    color: $text-off-black;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .custom-input {
    &::v-deep {
      .v-input__prepend-inner {
        margin-right: 8px !important;
      }

      .v-field--focused {
        color: $aqua;
      }
    }
  }

  .search-btn {
    border-radius: 8px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 4px 12px rgba(58, 103, 177, 0.3) !important;
  }

  // ============================================
  // TABLE CARD
  // ============================================
  .table-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .table-title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }

  .search-field {
    transition: all 0.3s ease;
    border-radius: 8px;

    &::v-deep input {
      font-size: 14px !important;
    }
  }

  .efficiency-table {
    &::v-deep {
      .v-data-table__wrapper {
        border-radius: 8px;
        overflow: hidden;
      }

      thead {
        background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;

        th {
          color: white !important;
          font-weight: 700 !important;
          font-size: 14px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          padding: 16px 16px !important;
          border: none !important;
          background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
          height: auto !important;
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid $border-color;
          transition: all 0.3s ease;

          &:hover {
            background-color: #f0f9ff !important;
          }

          td {
            padding: 12px 16px !important;
            color: $text-off-black;
            font-size: 14px !important;
            font-weight: 500;
          }
        }
      }
    }
  }

  .action-icon-btn {
    margin: 0 2px;
  }

  // ============================================
  // EMPTY STATE
  // ============================================
  .empty-state-card {
    border-radius: 16px;
    border: 2px dashed #e2e8f0;
    background: #f8fafc;
  }

  // Responsive Text
  @media (max-width: 960px) {
    .header-title {
      font-size: 24px;
    }

    .header-subtitle {
      font-size: 14px;
    }
  }
}
</style>